/**
 * @fileoverview Componente Molecule para mostrar un recordatorio individual del cliente en el Inbox.
 * Permite visualizar el texto, fecha, usuario y estado del recordatorio, con opción de expandir/cerrar y cambiar estado.
 * Cumple con Atomic Design y separación estricta de lógica y presentación.
 *
 * @module CustomerReminderItemMC
 * @category Component - Molecule
 * @since 3.1.0
 * @version 3.1.0
 *
 * @typedef {Object} CustomerReminderItemMCProps
 * @property {Object} reminder - Objeto recordatorio individual (con template_text, send_at, retailer_user, status, etc.)
 * @property {function} setCustomerReminders - Setter para actualizar la lista de recordatorios (Actions Layer)
 *
 * @component CustomerReminderItemMC
 * @description
 * **Características:**
 * - Visualiza el texto, fecha, usuario y estado del recordatorio
 * - Permite expandir/cerrar el texto largo (ShowMoreText)
 * - Permite cambiar el estado del recordatorio (CustomerReminderStatusSelectorMC)
 * - Cumple Atomic Design: Molecule
 * - Usa componentes globales: ShowMoreText, CustomerReminderStatusSelectorMC
 *
 * @param {CustomerReminderItemMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado del recordatorio
 *
 * @example
 * <CustomerReminderItemMC reminder={reminder} setCustomerReminders={setCustomerReminders} />
 *
 * @see {@link CustomerReminderStatusSelectorMC} Componente para cambiar estado
 * @see {@link ShowMoreText} Componente para expandir/cerrar texto
 */

import React, { memo } from 'react';
import ShowMoreText from 'react-show-more-text';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ReminderMediaPreviewMC from 'presentation/components/ReminderMediaPreviewMC';
import CustomerReminderStatusSelectorMC from './CustomerReminderStatusSelectorMC';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';


const CustomerReminderItemMC = memo(({ reminder, setCustomerReminders }) => {
  const { t } = useTranslation('InboxMC');

  // Debug tracking de dependencias críticas
  useTraceUpdate('CustomerReminderItemMC', {
    reminderId: reminder?.id,
    status: reminder?.status,
    setCustomerRemindersRef: setCustomerReminders
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper">
      <div className="tw-border tw-rounded-xl tw-px-4 tw-py-3 tw-border-gray-200">
        <ReminderMediaPreviewMC
          fileUrl={reminder.file_url}
          contentType={reminder.content_type}
          templateType={reminder.template_type}
          className="tw-mb-3"
        />
        {reminder.template_text ? (
          <ShowMoreText
            more={<span className="tw-text-blue-500">{t('reminder.show_more')}</span>}
            less={<span className="tw-text-blue-500">{t('reminder.show_less')}</span>}
            className="tw-text-xs tw-text-gray-950 tw-mb-4"
            expanded={false}
            truncatedEndingComponent="... "
          >
            <p className="tw-m-0 tw-text-xs tw-text-gray-950 tw-whitespace-pre-wrap">{reminder.template_text}</p>
          </ShowMoreText>
        ) : null}

        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span>{t('reminder.date')}</span>
          <span className="tw-ml-2">
            {moment(reminder.send_at, ['DD/MM/YYYY hh:mm a', moment.ISO_8601]).format('DD-MM-YYYY HH:mm')}
          </span>
        </div>

        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span>{t('reminder.sent_by')}</span>
          <span className="tw-ml-2">{reminder.retailer_user}</span>
        </div>

        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span className="tw-mr-1">{t('reminder.status')}</span>
          <CustomerReminderStatusSelectorMC
            reminder={reminder}
            setCustomerReminders={setCustomerReminders}
          />
        </div>
      </div>
    </div>
  );
});

CustomerReminderItemMC.displayName = 'CustomerReminderItemMC';

export default CustomerReminderItemMC;